import React, { useEffect, useRef, useState } from "react";

import { InputNumber as AntInput, InputNumberProps as AntInputNumberProps } from "antd";
import { RuleObject } from "antd/es/form";

import { parseInputRules } from "utils";

import { Form, InputLabel } from "components";

export type InputNumberProps = Pick<AntInputNumberProps, "decimalSeparator" | "disabled" | "precision" | "step" | "onBlur"> & {
  rules?: RuleObject[];
  validateFirst?: boolean;
  dataTestId?: string;
  className?: string;
  labelClassName?: string;
  defaultValue?: number | null;
  initialValue?: number | null;
  min?: number;
  max?: number;
  value?: number | null;
  label: string | React.ReactNode;
  tooltip?: string;
  message?: string;
  name?: string | string[];
  suffix?: string | React.ReactNode;
  placeholder?: string;
  onChange?: (value: number | null) => void;
  help?: React.ReactNode;
};

export const InputNumber = ({
  className,
  labelClassName = "",
  decimalSeparator = ",",
  defaultValue,
  disabled,
  initialValue,
  precision,
  label,
  tooltip,
  message,
  min,
  max,
  name,
  rules,
  validateFirst,
  step,
  suffix,
  value,
  dataTestId,
  placeholder,
  onBlur = () => {},
  onChange = (_value) => {},
  help,
}: InputNumberProps) => {
  const inputElement = useRef<HTMLInputElement>(null);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [internalValue, setInternalValue] = useState<number | null | undefined>(initialValue);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setHasBeenFocused(false);
    onBlur(e);
  };

  const handleChange = (value: number | null) => {
    setInternalValue(value);
    onChange(value);
  };

  const handleLabelClick = () => {
    setHasBeenFocused(true);
    inputElement.current?.focus();
  };

  const active = !!(hasBeenFocused || defaultValue || initialValue || initialValue === 0 || internalValue || internalValue === 0);
  const required = rules && rules[0] && rules[0].required;

  useEffect(() => {
    if (inputElement.current) {
      setInternalValue(inputElement.current.valueAsNumber);
    }
  }, [inputElement]);

  return (
    <div className={`input-inline-container ${hasBeenFocused ? "active" : ""}`}>
      <Form.Item
        className={`floating-input ${className} ${hasBeenFocused ? "active" : ""} ${disabled ? "disabled" : "message"}`}
        colon={false}
        label={<InputLabel active={active} required={required} label={label} className={labelClassName} handleClick={handleLabelClick} tooltip={tooltip} />}
        name={name}
        rules={parseInputRules(rules, message)}
        validateFirst={validateFirst ?? false}
        initialValue={initialValue || initialValue === 0 ? initialValue : defaultValue}
        help={help}
      >
        <AntInput
          data-testid={dataTestId}
          decimalSeparator={decimalSeparator || ","}
          disabled={disabled}
          min={min}
          max={max}
          precision={precision}
          ref={inputElement}
          step={step}
          value={value}
          placeholder={placeholder}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={() => setHasBeenFocused(true)}
        />
      </Form.Item>
      {suffix && <span className="klim-suffix">{suffix}</span>}
    </div>
  );
};
